import React from "react";
import { Card } from "./ui/card";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";

const PainReliefManagement = () => {
  const navigate = useNavigate();
  const handleBookAppointment = () => navigate("/book-appointment");

  const services = [
    "Chronic pain management programs",
    "Non-invasive manual therapies",
    "Posture and movement correction",
    "Patient education & self-care guidance",
  ];

  const benefits = [
    "Reduced pain and discomfort",
    "Improved flexibility and mobility",
    "Better posture and movement habits",
    "Confidence to return to your favorite activities",
  ];

  return (
    <>
      <Header hideBookButton={true} />
      <section className="py-20 bg-gradient-to-br from-white via-slate-50 to-blue-50 relative overflow-hidden min-h-screen">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#204080]/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#2E5AAC]/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-[#204080]/10 text-[#204080] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span>Pain Relief & Management</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-[#204080] mb-6 font-serif">
              Ready to Meet Your Care Specialists?
              <span className="block bg-clip-text bg-gradient-to-r from-[#204080] to-[#2E5AAC]">
                Move Better, Live Pain-Free
              </span>
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Proven, non-invasive treatments to help you move better and live pain-free.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-16 max-w-3xl mx-auto">
            {services.map((service, idx) => (
              <Card
                key={idx}
                className="flex items-center gap-4 p-6 bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <CheckCircle size={24} className="text-[#204080]" />
                <p className="text-gray-700 font-medium">{service}</p>
              </Card>
            ))}
          </div>

          {/* Benefits Section */}
          <Card className="max-w-3xl mx-auto bg-blue-50 p-8 rounded-lg mb-16">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">What You’ll Gain</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 text-lg">
              {benefits.map((benefit, idx) => (
                <li key={idx}>{benefit}</li>
              ))}
            </ul>
          </Card>

          {/* Success Metrics */}
          <Card className="p-8 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white border-0 shadow-2xl mb-16 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-4">Proven Pain Management Results</h3>
              <p className="text-blue-100 text-lg">Helping patients regain control and improve quality of life</p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold mb-2">88%</div>
                <div className="text-blue-100 text-sm">Reduction in Chronic Pain</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">92%</div>
                <div className="text-blue-100 text-sm">Improved Mobility</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">85%</div>
                <div className="text-blue-100 text-sm">Patient Satisfaction</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">1200+</div>
                <div className="text-blue-100 text-sm">Patients Treated</div>
              </div>
            </div>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <Card className="max-w-4xl mx-auto p-8 bg-white/90 backdrop-blur-sm border border-gray-200 shadow-2xl">
              <h3 className="text-2xl font-bold text-[#204080] mb-4">
                Start Your Recovery Today
              </h3>
              <p className="text-gray-600 mb-8 text-lg">
                Take the first step towards a pain-free life with our expert care.
              </p>
              <div className="flex justify-center">
                <button
                  className="flex items-center justify-center space-x-2 px-8 py-3 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  onClick={() => navigate('/book-appointment', { state: { selectedService: 'Pain Relief Session' } })}
                >
                  <span>Book Pain Relief Session</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </>
  );
};

export default PainReliefManagement;
